/**
 * FormField Component
 *
 * Wrapper component for consistent form field layout with labels, hints, and errors.
 */

import type { ReactNode } from 'react';

import { cn } from '../../utils/cn';
import { Label } from '../Label';

export interface FormFieldProps {
  /** Field label text */
  label: string;
  /** HTML for attribute - should match input id */
  htmlFor: string;
  /** Error message to display or boolean for error state */
  error?: string | boolean;
  /** Helper text shown below the field */
  hint?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Additional className for the container */
  className?: string;
  /** The form control element */
  children: ReactNode;
}

export function FormField({
  label,
  htmlFor,
  error,
  hint,
  required,
  className,
  children,
}: FormFieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={htmlFor} className="flex items-center gap-1">
        {label}
        {required && <span className="text-mv-red-600" aria-hidden="true">*</span>}
      </Label>
      {children}
      {hint && !error && (
        <p className="text-sm text-mv-gray-500" id={`${htmlFor}-hint`}>
          {hint}
        </p>
      )}
      {error && typeof error === 'string' && (
        <p className="text-sm text-mv-red-600" id={`${htmlFor}-error`} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
